import { SelectListEvent } from '@acies/core';
import { Account, Variant } from '@acies/domain';
import { IFlagJSON } from '@acies/schema';
import { FeatureFlagsAdminModel } from '../model/model';
import { FlagsService } from '../services/flags.service';

/** The VariantsController is responsible for the logic behind adding variants to the currently selected flag. */
export class VariantsController {
	#model: FeatureFlagsAdminModel;
	#service: FlagsService;
	public constructor(service: FlagsService, model: FeatureFlagsAdminModel) {
		this.#model = model;
		this.#service = service;
	}

	public organizationSelectionChange(event: Event): void {
		if (event instanceof SelectListEvent) {
			this.#model.selectedOrganization = event.selectedItem;
			this.#updateAccountsListData();
		}
	}

	public accountSelectionChange(event: Event): void {
		if (event instanceof SelectListEvent) {
			this.#model.selectedAccounts = event.selectedItems;
			this.#updateAddVariantsButtonEnabled();
		}
	}

	public addVariantsButtonClicked(): void {
		this.#showAddVariantsSpinner();
		this.#disabeAddVariantsButtons();
		this.#createNewVariantsAndAddToSelectedFlag();
		this.#updateServerVariants();
	}

	#updateServerVariants(): void {
		// get the model serialized as the IFlagJSON[] that the API accepts
		const body: IFlagJSON[] = this.#model.modelAsBodyJSON;
		this.#service.updateFlags(body).subscribe((value) => {
			this.#model.hideAddVariantsDialog();
			this.#hideAddVariantsSpinner();
			this.#enableAddVariantsCancelButton();
			if (value instanceof Error) {
				// if we get an error, revert the pendingVariants from the model
				this.#removePendingVariantsFromModel();
				this.#model.displayErrorDialogFromError(value);
				// notice that we don't reset the UI flags so the user can pick up
				// where they left with organization and accounts still selected
			} else {
				// we can now update the accounts list and remove the newly added variants from the UI
				this.#updateAccountsListData();
			}
			// we can remove the pending variants now we are finished handling the response
			this.#model.pendingVariants = [];
			// update the Variants list in the main UI
			this.#model.updateOrganizationVariants();
		});
	}

	/** If something goes wrong and we need to revert, we simply take the pending variants we added to the model and remove them again. */
	#removePendingVariantsFromModel(): void {
		const flag = this.#model.selectedFlag;
		if (flag) {
			const variants = flag.variants;
			for (const variant of this.#model.pendingVariants) {
				if (variants.includes(variant)) {
					const index = variants.indexOf(variant);
					variants.splice(index, 1);
				}
			}
		}
	}

	/** We want to add new variants from the selected accounts, so create instances, add them to the current selected flag and keep them in a pending array, so we can revert the model changes if something goes wrong. */
	#createNewVariantsAndAddToSelectedFlag(): void {
		const flag = this.#model.selectedFlag;
		if (flag) {
			const enabled = false;
			for (const account of this.#model.selectedAccounts) {
				const variant = new Variant({ account, enabled });
				this.#model.pendingVariants.push(variant);
				flag.variants.push(variant);
			}
			// we update the displayed variants after the flag instance has been mutated.
			this.#model.updateOrganizationVariants();
		}
	}

	#showAddVariantsSpinner(): void {
		this.#model.addVariantsSpinnerVisible = true;
	}

	#hideAddVariantsSpinner(): void {
		this.#model.addVariantsSpinnerVisible = false;
	}

	#disabeAddVariantsButtons(): void {
		this.#model.addVariantsCancelButtonEnabled = false;
		this.#model.addVariantsButtonEnabled = false;
	}

	#enableAddVariantsCancelButton(): void {
		this.#model.addVariantsCancelButtonEnabled = true;
	}

	#updateAddVariantsButtonEnabled(): void {
		this.#model.addVariantsButtonEnabled = this.#model.selectedAccounts.length > 0;
	}

	/** We only want accounts in the list that hasn't already been added as a variant. */
	#updateAccountsListData(): void {
		const organization = this.#model.selectedOrganization;
		if (organization) {
			this.#model.organizationAccounts = organization.accounts.filter(this.#filterAccount.bind(this));
		} else {
			this.#model.organizationAccounts = [];
		}
	}

	/**
	 * When an Organization is selected, we only want to show the accounts that hasn't already been added as a variant of the current selected flag we are editing.
	 *
	 * Since all domain entities are in memory and references each other by pointers, we can compare by reference and not value, in this case, variant.account === account, will work correctly.
	 */
	#filterAccount(account: Account): boolean {
		if (this.#model.selectedFlag) {
			for (const variant of this.#model.selectedFlag.variants) {
				if (variant.account === account) {
					return false;
				}
			}
		}
		return true;
	}
}
